/**
 * Typed HTTP contracts (plan §4.9, D17): every route is declared once as a contract that the API
 * mounts (`defineRoute(app, deps, contract, handler)`) and the client calls
 * (`api.call(contract, { params, query, body })`). Path parameters are inferred from the path
 * template; params, query and body are Zod schemas; the response schema types the client and is
 * validated by the server in development and e2e (`DR_VALIDATE_RESPONSES`).
 *
 * Wire rules:
 * - `params` and `query` arrive as strings. Use `z.uuid()`/`z.string()` for params and the
 *   query codecs of `./query` (`q.int()`, `q.bool()`, `q.list()`, `q.json()`) for typed query
 *   values, so that the client can pass numbers, booleans, arrays and objects.
 * - `body` is JSON. `response` describes the JSON the handler returns: no transforms that change
 *   the shape, dates as ISO strings. `z.void()` means "204 No Content".
 */
import { z } from 'zod';

export const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
export type HttpMethod = (typeof HTTP_METHODS)[number];

// -- path templates -----------------------------------------------------------------------------

/** Parameter names of a path template: `'/objects/:key/records/:id'` → `'key' | 'id'`. */
export type PathParamNames<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? Name | PathParamNames<`/${Rest}`>
  : P extends `${string}:${infer Name}`
    ? Name
    : P extends `${string}/*`
      ? '*'
      : never;

/** Path parameter values as they travel in the URL. */
export type PathParams<P extends string> = { [K in PathParamNames<P>]: string };

const PARAM_RE = /:([A-Za-z_][A-Za-z0-9_]*)/g;

/** Parameter names of a path template at runtime. */
export function pathParamNames(path: string): string[] {
  const names = [...path.matchAll(PARAM_RE)].map((m) => m[1]!);
  if (path.endsWith('/*')) names.push('*');
  return names;
}

/**
 * Fill a path template: every `:name` is replaced by its URI-encoded value; a missing or empty
 * value throws (a request to `/objects//records` must never leave the client).
 */
export function buildPath(path: string, params: Record<string, unknown> | undefined): string {
  const filled = path.replace(PARAM_RE, (_m, name: string) => {
    const v = params?.[name];
    if (v === undefined || v === null || v === '')
      throw new Error(`Missing path parameter "${name}" for ${path}`);
    return encodeURIComponent(String(v));
  });
  if (!filled.endsWith('/*')) return filled;
  const rest = params?.['*'];
  if (rest === undefined || rest === null) throw new Error(`Missing path parameter "*" for ${path}`);
  return `${filled.slice(0, -1)}${String(rest)
    .split('/')
    .map((s) => encodeURIComponent(s))
    .join('/')}`;
}

// -- contracts ----------------------------------------------------------------------------------

type AnySchema = z.ZodType;

/** Default params schema of a path: one non-empty string per parameter. */
export type DefaultParamsSchema<P extends string> = z.ZodObject<{ [K in PathParamNames<P>]: z.ZodString }>;

export type ContractDefinition<
  M extends HttpMethod,
  P extends `/${string}`,
  PS extends AnySchema | undefined,
  QS extends AnySchema | undefined,
  BS extends AnySchema | undefined,
  RS extends AnySchema,
> = {
  method: M;
  /** Relative to `/api/v1`, Fastify syntax: `/objects/:key/records/:id`. */
  path: P;
  /** Schema of the path parameters (keys must match the template); default: non-empty strings. */
  params?: PS;
  query?: QS;
  /** Request body (JSON); omit for routes without a body. */
  body?: BS;
  /** Response body (JSON). `z.void()` → 204 No Content. */
  response: RS;
  /** One-line summary (OpenAPI, route catalog, MCP tool description). */
  summary: string;
  /** Longer description (OpenAPI). */
  description?: string;
  /** Permission required (e.g. `metadata.write`); `workspace.admin` always passes. */
  permission?: string;
  /** No authentication required. */
  public?: boolean;
  /** Success status (default 200, or 204 when the response is `z.void()`). */
  status?: number;
};

export type Contract<
  M extends HttpMethod = HttpMethod,
  P extends `/${string}` = `/${string}`,
  PS extends AnySchema = AnySchema,
  QS extends AnySchema | undefined = AnySchema | undefined,
  BS extends AnySchema | undefined = AnySchema | undefined,
  RS extends AnySchema = AnySchema,
> = {
  readonly kind: 'dataroom.contract';
  readonly method: M;
  readonly path: P;
  readonly params: PS;
  readonly query: QS;
  readonly body: BS;
  readonly response: RS;
  readonly summary: string;
  readonly description: string | undefined;
  readonly permission: string | undefined;
  readonly public: boolean;
  readonly status: number;
  /** Path parameter names in template order. */
  readonly paramNames: readonly string[];
  /** Whether the success response has no body (204). */
  readonly noContent: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the widest contract type for generic helpers
export type AnyContract = Contract<HttpMethod, `/${string}`, any, any, any, any>;

/**
 * Params schemas must accept exactly what the URL carries: an object whose input keys are the
 * template's parameter names, with string values.
 */
type ParamsSchemaFor<P extends string> = z.ZodType<unknown, PathParams<P>>;

/**
 * Declare a route contract. Path parameters are inferred from the template; a `params` schema
 * (for example `z.object({ id: z.uuid() })`) must cover exactly those names.
 *
 *   export const navContract = defineContract({
 *     method: 'GET', path: '/nav', response: NavDTO, summary: 'Navigation rails',
 *   });
 */
export function defineContract<
  const M extends HttpMethod,
  const P extends `/${string}`,
  RS extends AnySchema,
  PS extends ParamsSchemaFor<P> | undefined = undefined,
  QS extends AnySchema | undefined = undefined,
  BS extends AnySchema | undefined = undefined,
>(
  def: ContractDefinition<M, P, PS, QS, BS, RS>,
): Contract<M, P, PS extends AnySchema ? PS : DefaultParamsSchema<P>, QS, BS, RS> {
  if (!HTTP_METHODS.includes(def.method)) throw new Error(`Unsupported method ${String(def.method)}`);
  if (!def.path.startsWith('/') || def.path.startsWith('/api/'))
    throw new Error(`Contract path "${def.path}" must be relative to /api/v1 and start with "/"`);
  const names = pathParamNames(def.path);
  if (new Set(names).size !== names.length) throw new Error(`Duplicate path parameter in ${def.path}`);
  if (def.body && (def.method === 'GET' || def.method === 'DELETE'))
    throw new Error(`${def.method} ${def.path}: GET and DELETE contracts cannot have a body`);
  let params: AnySchema;
  if (def.params) {
    params = def.params;
    const shape = (def.params as unknown as { shape?: Record<string, unknown> }).shape;
    if (shape) {
      const keys = Object.keys(shape).sort();
      if (keys.join(',') !== [...names].sort().join(','))
        throw new Error(
          `${def.method} ${def.path}: params schema keys (${keys.join(', ')}) must match the path parameters (${names.join(', ')})`,
        );
    }
  } else {
    params = z.object(Object.fromEntries(names.map((n) => [n, z.string().min(1)])));
  }
  const noContent = def.response instanceof z.ZodVoid || def.response instanceof z.ZodUndefined;
  return Object.freeze({
    kind: 'dataroom.contract' as const,
    method: def.method,
    path: def.path,
    params,
    query: def.query,
    body: def.body,
    response: def.response,
    summary: def.summary,
    description: def.description,
    permission: def.permission,
    public: Boolean(def.public),
    status: def.status ?? (noContent ? 204 : 200),
    paramNames: Object.freeze(names),
    noContent,
  }) as unknown as Contract<M, P, PS extends AnySchema ? PS : DefaultParamsSchema<P>, QS, BS, RS>;
}

export function isContract(value: unknown): value is AnyContract {
  return (
    typeof value === 'object' && value !== null && (value as { kind?: unknown }).kind === 'dataroom.contract'
  );
}

/** `METHOD /api/v1/path` — how errors and logs name a route. */
export function contractName(contract: AnyContract, prefix = '/api/v1'): string {
  return `${contract.method} ${prefix}${contract.path}`;
}

// -- input and output types ---------------------------------------------------------------------

type InputOf<S> = S extends AnySchema ? z.input<S> : never;
type OutputOf<S> = S extends AnySchema ? z.output<S> : undefined;

/** Path parameters as the client passes them. */
export type ContractParamsInput<C extends AnyContract> = InputOf<C['params']>;
/** Query as the client passes it (typed values; the client serialises them). */
export type ContractQueryInput<C extends AnyContract> = InputOf<C['query']>;
/** Request body as the client sends it. */
export type ContractBodyInput<C extends AnyContract> = InputOf<C['body']>;

/** Parsed path parameters on the server. */
export type ContractParams<C extends AnyContract> = z.output<C['params']>;
/** Parsed query on the server (`undefined` without a query schema). */
export type ContractQuery<C extends AnyContract> = OutputOf<C['query']>;
/** Parsed body on the server (`undefined` without a body schema). */
export type ContractBody<C extends AnyContract> = OutputOf<C['body']>;

/** What the server handler returns: the JSON value before serialisation (void for 204). */
export type ContractResult<C extends AnyContract> = C['response'] extends z.ZodVoid | z.ZodUndefined
  ? void
  : z.input<C['response']>;
/** What the client receives. */
export type ContractResponse<C extends AnyContract> = z.output<C['response']>;

type Part<K extends string, S, In> = [S] extends [undefined]
  ? { [P in K]?: never }
  : // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- `{}` = "nothing required"
    {} extends In
    ? { [P in K]?: In }
    : { [P in K]: In };

type ParamsPart<C extends AnyContract> = [PathParamNames<C['path']>] extends [never]
  ? { params?: never }
  : { params: ContractParamsInput<C> };

/** The second argument of `api.call(contract, input)`. */
export type ContractInput<C extends AnyContract> = ParamsPart<C> &
  Part<'query', C['query'], ContractQueryInput<C>> &
  Part<'body', C['body'], ContractBodyInput<C>>;

/** Whether `input` may be omitted entirely. */
export type InputOptional<C extends AnyContract> =
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- `{}` = "nothing required"
  {} extends ContractInput<C> ? true : false;
